import React, { useContext, useState, useEffect } from "react";
import _ from "lodash";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import MigrationRunDetailModal from "Components/MigrationRunDetailModal";
import ButtonBack from "Components/buttonBack/ButtonBack";
import DetailInfo from "Components/detailInfo/DetailInfo";
import t from "Utils/translation";
import { displayFormattedDate } from "Utils/dateFormats";
import { Icon, migrations as migrationsIcon, refresh } from "Icons/index";
import cx from "classnames";
import s from "./resourcesMigrations.scss";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { AuthContext, MediaQueriesContext } from "../../App";
import { useApiQuery } from "../../hooks";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { isVianovaCustomer } from "@/utils/vianova";

const RunningTag = (props) => {
  const { status, scheduled_at, progress_percentage, showRun, code, operation_meta_type, execute_now } = props;

  const showDetails = ["error", "crashed", "success"].indexOf(status) > -1;

  return (
    <div
      className={cx(s.runningTag, {
        [s.tagProgress]: status === "running",
        [s.tagSuccess]: status === "success",
        [s.tagError]: status === "error" || status === "crashed",
        [s.tagScheduled]: status === "scheduled" || !status,
      })}
    >
      {status === "running" && <Icon className={s.syncIcon} svg={refresh} width={"0.75rem"} height={"0.75rem"} />}
      <div className={s.runStatus}>
        {status
          ? status === "scheduled"
            ? `${displayFormattedDate(scheduled_at)}`
            : status === "error" || status === "crashed"
            ? t("qbmove.status.failed")
            : status === "success"
            ? t("qbmove.status.completed")
            : operation_meta_type == "test"
            ? t("qbmove.status.runningTest")
            : t("qbmove.status.running", null, { percentage: progress_percentage || 0 })
          : execute_now
          ? t("qbmove.status.soon")
          : "-"}
      </div>

      {code && showDetails && (
        <div className={s.runDetailsBtn} onClick={() => showRun()}>
          {t("migrations.resources.details")}
        </div>
      )}
    </div>
  );
};

const MigrationResourceScreen = (props) => {
  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);
  const { isMediaMobile } = useContext(MediaQueriesContext);

  const migrationCode = props.match.params?.migrationCode;

  const history = useHistory();
  const location = useLocation();

  const [modalOpened, setModalOpened] = useState(false);
  const [showInfo, setShowInfo] = useState(isMediaMobile ? false : true);
  const [selectedRun, setSelectedRun] = useState(null);

  const {
    data: migrationResources,
    isLoading: areMigrationResourcesLoading,
    pagination: resourcesPaginationInfo,
    getData: getMigrationResources,
  } = useApiQuery("getMigrationResources", {
    enablePagination: true,
    requestParams: migrationCode,
    autoRefresh: {
      shouldRefresh: handleNeedRefresh,
      interval: 8000,
    },
  });
  const { data: migration } = useApiQuery("getMigration", {
    requestParams: migrationCode,
  });

  const handleNeedRefresh = () => {
    if (!Array.isArray(migrationResources)) return false;

    return migrationResources.some(
      (res) =>
        res.test_run?.status === "running" ||
        res.first_execution_run?.status === "running" ||
        res.second_execution_run?.status === "running"
    );
  };
  const handleOpenRunDetail = (run) => {
    setSelectedRun(run);
    setModalOpened(true);
  };
  const handleCloseLogModal = () => {
    setModalOpened(false);
  };
  const handleToggleInfo = () => setShowInfo((prev) => !prev);
  const handleGoBack = () => {
    const query = _.get(location, "state.prevPagination", "");
    history.push("/migrations" + query);
  };

  useEffect(() => {
    setShowInfo(isMediaMobile ? false : true);
  }, [isMediaMobile]);

  return (
    <div className={s.resourceMigrations}>
      <div className={s.navigation}>
        <ButtonBack onClick={handleGoBack} />
        <div className={s.viewSectionLabel}>{t("section.view.migration.resources")}</div>
      </div>

      <div className={s.header}>
        <div className={s.wrapTitle}>
          <div className="sectionTitle">{t("migrations.resources.header.title")}</div>
          {isMediaMobile && (
            <div className={cx(s.showInfo, "internalLink")} onClick={handleToggleInfo}>
              {showInfo ? t("button.details.hide") : t("button.details.show")}
            </div>
          )}
        </div>
        {showInfo && migration && (
          <div>
            <div className={s.info}>
              <DetailInfo label={t("detailInfo.label.code")} info={migration.code} inLine />
              <DetailInfo label={t("detailInfo.label.type")} info={migration.preset} inLine />
              <DetailInfo
                label={t("detailInfo.label.migration.first_execution")}
                info={displayFormattedDate(migration.first_execution_date)}
                inLine
              />
              <DetailInfo
                label={t("detailInfo.label.migration.second_execution")}
                info={displayFormattedDate(migration.second_execution_date) || "-"}
                inLine
              />
            </div>
            {isLoggedUserAdmin() && migration && (
              <div className={s.downloadFileLink}>
                <a target="_blank" href={`${BASE_URL}/migrations/${migration.code}/file`} rel="noreferrer">
                  Scarica file .csv usato per la migrazione
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={s.settings}>
        <div className={s.warningBanner}>
          {migration && migration.is_late && (
            <WarningBanner
              info
              color="blue"
              msg={
                "L'esecuzione della migrazione inizierà presto! Il sistema sta completando l'esecuzione di altre migrazioni."
              }
            />
          )}
        </div>

        <FullPaginationControls
          className={s.filters}
          paginationInfo={resourcesPaginationInfo}
          location={location}
          refreshList={() => getMigrationResources(migrationCode)}
          listFilters={["all", "failed", "success", "running"]}
          labelPrefix="migrationResources"
          listFilterLegends={{
            all: <Icon svg={migrationsIcon} width="0.75rem" height="0.75rem" />,
            success: <DomainStatusBadge status="success" isHealthy showAsPreview />,
            failed: <DomainStatusBadge status="error" isHealthy showAsPreview />,
            running: <DomainStatusBadge status="updating" showAsPreview />,
          }}
          isLoading={areMigrationResourcesLoading}
          totals={resourcesPaginationInfo.totals}
          showPaginationFilter={false}
          paginationShowOptions={[
            { value: 25, label: 25 },
            { value: 50, label: 50 },
            { value: 100, label: 100 },
            { value: 100000, label: t("all") },
          ]}
        />

        <div className={s.table}>
          <ResponsiveTable
            verticalOnMobile
            history={history}
            areRowsLoading={areMigrationResourcesLoading}
            location={location}
            headers={[
              { name: t("table.header.account") },
              {
                name: t("qbmove.test_run"), // test autenticazione
              },
              {
                name: t("qbmove.first_execution"), // prima esecuzione
              },
              {
                name: t("qbmove.second_execution"), // seconda esecuzione
              },
            ]}
            rows={_.map(migrationResources, (mig) => {
              return (
                <tr key={mig.id}>
                  <td data-label={t("table.header.account")}>
                    {mig.local_email} <span className={s.accentArrow}>←</span> {mig.remote_username}{" "}
                    <span className={s.host}> - {mig.remote_server}</span>{" "}
                  </td>
                  <td data-label={t("table.header.account")}>
                    <RunningTag {...mig.test_run} showRun={() => handleOpenRunDetail(mig.test_run)} />
                  </td>
                  <td data-label={t("table.header.account")}>
                    <RunningTag
                      {...mig.first_execution_run}
                      execute_now={migration && migration.execute_now}
                      showRun={() => handleOpenRunDetail(mig.first_execution_run)}
                    />
                  </td>
                  <td data-label={t("table.header.account")}>
                    {mig.second_execution_run ? (
                      <RunningTag
                        {...mig.second_execution_run}
                        showRun={() => handleOpenRunDetail(mig.second_execution_run)}
                      />
                    ) : migration && migration.second_execution_date ? (
                      <div className={cx(s.runningTag, s.tagScheduled)}>
                        <div className={s.runStatus}>{displayFormattedDate(migration.second_execution_date)}</div>
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              );
            })}
          />
        </div>
      </div>

      <MigrationRunDetailModal
        run={selectedRun}
        opened={modalOpened}
        onClose={handleCloseLogModal}
        migrationCode={props.match.params.migrationCode}
        canSeeLog={isLoggedUserAdmin() || isVianovaCustomer(getLoggedUser().code)}
      />
    </div>
  );
};

export default MigrationResourceScreen;
